import { useCallback, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import { useAnalytics } from "../../analytics/analytics";
import { SupabaseMomentService } from "../../moment/server/supabase-moment-service";

import type { UUID } from "../../../utils/uuid";

/**
 * Moves a Communication to another program or folder, one move at a time so that a move and an Undo
 * cannot race, and says if it failed. Resolves to whether it was done.
 */
export function useMoveCommunication(): {
  move: (id: UUID, programId: UUID) => Promise<boolean>;
  moving: boolean;
} {
  const [moving, setMoving] = useState(false);
  const busy = useRef(false);
  const { gaEvent } = useAnalytics();

  const update = useSupabaseCallback(
    async ({ supabase }, id: UUID, programId: UUID): Promise<boolean> => {
      const { data, error } = await new SupabaseMomentService(supabase).update(id, {
        program_id: programId,
      });

      if (error || !data) {
        toast.error("Something went wrong moving the Communication. Please try again.");
        return false;
      }

      gaEvent("change_moment_program", { moment_id: id, event_category: "moment" });
      return true;
    },
    [gaEvent],
  );

  const move = useCallback(
    async (id: UUID, programId: UUID): Promise<boolean> => {
      if (busy.current) return false;
      busy.current = true;
      setMoving(true);
      try {
        return (await update(id, programId)) ?? false;
      } finally {
        busy.current = false;
        setMoving(false);
      }
    },
    // oxlint-disable-next-line react/memo-dependencies -- it is used; the rule misses uses inside try
    [update],
  );

  return { move, moving };
}
